/**
 * Le schéma d'un SOLIDE ILLUSTRÉ (`CAP-11`, `fiches/solide.ts`) — ce qu'un
 * auteur écrit dans une fiche, dans un énoncé, ou dans l'option d'un choix. Un
 * seul schéma pour les trois : `fiche-cours.schema.ts` et `manuel-exercices.ts`
 * l'importent.
 *
 * Le schéma tient la FORME ; ce qu'il ne voit pas (un triangle qui ne ferme
 * pas, un patron qui ne se replie pas, une cote que la vue ne montre pas) est
 * dit par `griefsDeSolide`.
 */
import { z } from 'zod';
import { GRANDEURS_DE_SOLIDE, POSES_DE_DEMI_BOULE, VUES_DE_SOLIDE, } from '../fiches/solide.js';
const longueur = z.number().finite().positive();
const cube = z
    .object({
    forme: z.literal('cube'),
    arete: longueur,
    patron: z
        .array(z.tuple([z.number().int(), z.number().int()]))
        .min(4)
        .max(8)
        .optional(),
})
    .strict();
const pave = z
    .object({ forme: z.literal('pave'), longueur, largeur: longueur, hauteur: longueur })
    .strict();
const prisme = z
    .object({
    forme: z.literal('prisme_triangulaire'),
    cotes: z.tuple([longueur, longueur, longueur]),
    hauteur: longueur,
    angleDroit: z.boolean().optional(),
})
    .strict();
/* La hauteur OU l'apothème : « l'un, pas les deux » est contrôlé par `griefsDeSolide`. */
const pyramide = z
    .object({
    forme: z.literal('pyramide'),
    cote: longueur,
    hauteur: longueur.optional(),
    apotheme: longueur.optional(),
})
    .strict();
const cylindre = z
    .object({ forme: z.literal('cylindre'), rayon: longueur, hauteur: longueur })
    .strict();
/* `renverse` (tranche T37) : pointe en bas — le BAS d'un cornet, d'une toupie (`griefsDeSolide`). */
const cone = z
    .object({
    forme: z.literal('cone'),
    rayon: longueur,
    hauteur: longueur,
    renverse: z.boolean().optional(),
})
    .strict();
const boule = z.object({ forme: z.literal('boule'), rayon: longueur }).strict();
/* La demi-boule (tranche T37) : `dome` (défaut), la face plane en bas ; `bol`, en haut. */
const demiBoule = z
    .object({
    forme: z.literal('demi_boule'),
    rayon: longueur,
    pose: z.enum(POSES_DE_DEMI_BOULE).optional(),
})
    .strict();
/* Ce qui tient sur quoi (un corps rond sur un cylindre, un cône sur un pavé…) : `griefsDeSolide`. */
const compose = z
    .object({
    forme: z.literal('compose'),
    bas: z.discriminatedUnion('forme', [cube, pave, cylindre, cone]),
    haut: z.discriminatedUnion('forme', [cube, pave, cylindre, pyramide, cone, boule, demiBoule]),
})
    .strict();
export const solideSchema = z.discriminatedUnion('forme', [
    cube,
    pave,
    prisme,
    pyramide,
    cylindre,
    cone,
    boule,
    demiBoule,
    compose,
]);
export const solideDecritSchema = z
    .object({
    decrit: z.string().min(20).max(900),
    solide: solideSchema,
    vue: z.enum(VUES_DE_SOLIDE),
    unite: z.string().trim().min(1).max(6).optional(),
    cotes: z
        .array(z
        .object({
        grandeur: z.enum(GRANDEURS_DE_SOLIDE),
        texte: z.string().trim().min(1).max(18).optional(),
        partie: z.enum(['bas', 'haut']).optional(),
    })
        .strict())
        .max(6)
        .optional(),
    faces: z.boolean().optional(),
})
    .strict();
//# sourceMappingURL=solide.schema.js.map